import { CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  useFindExternalDataForAccountMutation,
  useGetSuggestTitlesMutation,
} from "../../../../api";
import { JobTitle } from "../../../../types/apiTypes";
import { CustomToast } from "../../CustomToast";
import { ReuseModal } from "../../ReuseModal";
import { JobTitlesListHeader } from "./JobTitleListHeader";
import { JobTitlesSortableList } from "../../../features/JobTitlesSortableList";
import { KeyRoles } from "../../../../types/enums";
import { StarsOutlineIcon } from "../../../images";
import { CustomButton } from "../../CustomButton";
import { ReuseTooltip } from "../../ReuseTooltip";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { useParams } from "react-router-dom";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initTitles: JobTitle[];
  account_id: string;
  onSuccess: () => void;
  role?: KeyRoles;
  withSuggestions?: boolean;
};

export const GetMoreContactsModal = ({
  isOpen,
  setIsOpen,
  initTitles,
  account_id,
  onSuccess,
  role,
  withSuggestions,
}: Props) => {
  const { campaign_id } = useParams();

  const [titles, setTitles] = useState<JobTitle[]>(initTitles);
  const [createdTitleId, setCreatedTitleId] = useState<string>("");
  const [searchTitleId, setSearchTitleId] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchTitles, setSearchTitles] = useState<string[]>([]);
  const [isNoSuggestions, setIsNoSuggestions] = useState<boolean>(false);

  const [findExternalDataForAccount, { isLoading }] =
    useFindExternalDataForAccountMutation();
  const [getSuggestTitles, { isLoading: isSuggestLoading }] =
    useGetSuggestTitlesMutation();

  useEffect(() => {
    setTitles(initTitles);
  }, [initTitles]);

  useEffect(() => {
    const searchTitleList = titles
      .filter(({ title }) =>
        title.toLowerCase().includes(searchValue.toLowerCase()),
      )
      .map(({ id }) => id);
    setSearchTitles(searchTitleList);
    setSearchTitleId(searchValue ? searchTitleList[0] : "");
  }, [searchValue, titles]);

  const addTitleHandler = (title: string, successHandler?: () => void) => {
    const id = Date.now().toString();
    if (
      titles
        .filter((el) => el.id !== id)
        .some(
          (el) => el.title.trim().toLowerCase() === title.trim().toLowerCase(),
        )
    ) {
      toast.error(
        <CustomToast
          type="error"
          title="Job Title Update Failed"
          text="Each job title must be unique. Please choose a different title."
        />,
      );
      return;
    }

    setTitles((titles) => [
      ...titles,
      { id, title, is_critical: true, is_edit: false },
    ]);
    setCreatedTitleId(id);

    successHandler && successHandler();
  };

  const submitHandler = () => {
    if (
      titles
        .filter(({ is_suggest }) => !is_suggest)
        .some(({ is_edit }) => is_edit)
    ) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid Job Title"
          text="Please complete editing before proceeding."
        />,
      );
      return;
    }

    findExternalDataForAccount({
      account_id: account_id!,
      campaign_id: campaign_id!,
      title_gaps: titles
        .filter(({ is_suggest }) => !is_suggest)
        .map(({ title, is_critical }) => ({
          name: title,
          is_critical,
        })),
      role,
    }).then(() => {
      onSuccess();
      setIsOpen(false);
    });
  };

  const isNoTitles = titles.length === 0;

  const setSearchElIndexHandler = (index: number) => {
    setSearchTitleId(searchTitles[index]);
  };

  const addSuggests = () => {
    const existingTitles = titles.filter(({ is_suggest }) => !is_suggest);

    getSuggestTitles({
      role,
      existing_titles: existingTitles.map(({ title }) => title),
      account_id,
    })
      .unwrap()
      .then(({ suggestions }) => {
        if (suggestions.length === 0) {
          setIsNoSuggestions(true);
          return;
        }
        const preperTitles = suggestions.map((title) => ({
          id: title,
          title,
          is_critical: true,
          is_edit: true,
          is_suggest: true,
        }));

        setTitles([...existingTitles, ...preperTitles]);
        setCreatedTitleId(suggestions[suggestions.length - 1]);
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            title="Failed to find more suggestions."
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
          />,
        );
        setIsNoSuggestions(true);
      });
  };

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Explore INFUSE Audience Expansion"
      maxWidth="548px"
      submitBtnText="Expand Audience"
      submitBtnColor="#1C3C6C"
      onSubmit={submitHandler}
      isLoading={isLoading}
      isDisabled={
        isLoading || titles.filter(({ is_suggest }) => !is_suggest).length === 0
      }
    >
      <Stack gap="16px">
        <Stack gap="24px">
          <Stack
            flexDirection="row"
            gap="16px"
            alignItems="flex-end"
            justifyContent="space-between"
            width="100%"
          >
            <Typography component="span" variant="body1" color="text.secondary">
              Customize the list of titles for audience expansion.
            </Typography>
            {withSuggestions && (
              <ReuseTooltip
                text={
                  isNoSuggestions
                    ? "There are currently no new job titles to suggest"
                    : ""
                }
                placement="top"
                hideChildren={false}
              >
                <CustomButton
                  onClick={addSuggests}
                  variant="outlined"
                  customColor="#FFFFFF"
                  textColor="#BD4D00"
                  customSize="fit"
                  type="button"
                  startIcon={
                    isSuggestLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      !isNoSuggestions && <StarsOutlineIcon />
                    )
                  }
                  disabled={isNoSuggestions || isSuggestLoading}
                  sx={{
                    height: "42px",
                    borderColor: "#BD4D00",
                    minWidth: "170px",
                    maxWidth: "170px",
                  }}
                >
                  {isNoSuggestions ? "No Suggestions" : "Suggest Titles"}
                </CustomButton>
              </ReuseTooltip>
            )}
          </Stack>

          <JobTitlesListHeader
            addTitleHandler={addTitleHandler}
            setSearchValue={setSearchValue}
            searchTotalCount={searchTitles.length}
            searchElIndex={searchTitles.findIndex((el) => searchTitleId === el)}
            setSearchElIndex={setSearchElIndexHandler}
            isLimit={titles.length >= 100}
          />
        </Stack>
        {!isNoTitles && (
          <JobTitlesSortableList
            titles={titles}
            setTitles={setTitles}
            createdTitleId={createdTitleId}
            setCreatedTitleId={setCreatedTitleId}
            searchTitleId={searchTitleId}
          />
        )}
      </Stack>
    </ReuseModal>
  );
};
